import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { Container, Navbar, Nav, NavDropdown, Modal, Button } from 'react-bootstrap';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './components/Login';
import Register from './components/Register';
import UserManagement from './components/UserManagement';
import ChangePassword from './components/ChangePassword';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import ClientList from './components/ClientList';
import EquipmentList from './components/EquipmentList';
import EventList from './components/EventList';
import Dashboard from './components/Dashboard';
import ConsumablesList from './components/ConsumablesList';
import EventCalendar from './components/EventCalendar';
import EventSpecificationList from './components/EventSpecificationList';
import EventPreparation from './components/EventPreparation';
import EventEquipmentManager from './components/EventEquipmentManager';
import LocationList from './components/LocationList';
import CategoryList from './components/CategoryList';
// נקודה 6: הוסר הייבוא של RoleList
import EventColorList from './components/EventColorList';
import EventDetailsPage from './components/EventDetailsPage';
import EventForm from './components/EventForm';
import WarehouseTaskList from './components/WarehouseTaskList';
import TestPage from './components/TestPage';
import DefectiveEquipmentManagement from './components/DefectiveEquipmentManagement';
import EventsSearchPage from './components/EventsSearchPage';
import './App.css';

// Component wrapper for navigation
function AppContent() {
  const { 
    user, 
    isAuthenticated, 
    logout, 
    canManageUsers,
    canEditEquipment,
    canEditEvents
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showEventFormForNewEvent, setShowEventFormForNewEvent] = useState<boolean>(false);
  const [initialNewEventDates, setInitialNewEventDates] = useState<{ startDate: Date, endDate: Date } | null>(null);
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'dark') return true;
    if (savedTheme === 'light') return false;
    return false;
  });

  useEffect(() => {
    const theme = isDarkMode ? 'dark' : 'light';
    document.documentElement.setAttribute('data-bs-theme', theme);
    document.body.classList.toggle('dark-mode', isDarkMode);
    localStorage.setItem('theme', theme);
  }, [isDarkMode]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // ניווט אוטומטי לפי תפקיד המשתמש
  useEffect(() => {
    if (isAuthenticated && user && location.pathname === '/') {
      const defaultPage = user.default_page || '/dashboard';
      if (defaultPage !== '/dashboard') {
        navigate(defaultPage);
      }
    }
  }, [isAuthenticated, user, location.pathname, navigate]);
  
  const handleNavigateToEquipmentManager = (eventId: number) => {
    navigate(`/manage-event-equipment/${eventId}`);
  };
  
  const handleNavigateToEventPreparation = (eventId: number) => {
    navigate(`/event-preparation/${eventId}`);
  };
  
  const handleNavigateToEventDetails = (eventId: number) => {
      navigate(`/event-details/${eventId}`);
  };

  const handleCreateNewEvent = (startDate: Date, endDate: Date) => {
    setInitialNewEventDates({ startDate, endDate });
    setShowEventFormForNewEvent(true);
  };

  const handleNewEventFormSuccess = (savedEvent: import('./interfaces').Event) => {
    setShowEventFormForNewEvent(false);
    setInitialNewEventDates(null);
    if (savedEvent.id) {
      navigate(`/event-details/${savedEvent.id}`);
    } else {
      navigate('/events');
    }
  };

  const handleNewEventFormCancel = () => {
    setShowEventFormForNewEvent(false);
    setInitialNewEventDates(null);
    navigate('/calendar');
  };

  const handlePageChange = (page: string) => {
    navigate(`/${page}`);
  };

  const authPagesWithoutNavbar = ['/login', '/register', '/forgot-password'];
  const isResetPasswordPage = location.pathname.startsWith('/reset-password/');
  const shouldHideNavbar = authPagesWithoutNavbar.includes(location.pathname) || isResetPasswordPage;

  return (
    <div className="App">
      {!shouldHideNavbar && (
        <Navbar bg="dark" variant="dark" expand="lg" fixed="top" className="app-main-navbar shadow-sm">
          <Container fluid>
            <Navbar.Brand onClick={() => handlePageChange('dashboard')} style={{cursor: 'pointer'}}>מערכת ניהול מחסן</Navbar.Brand>
            <Navbar.Toggle aria-controls="basic-navbar-nav" />
            <Navbar.Collapse id="basic-navbar-nav">
              <Nav className="me-auto">
                {canEditEquipment || canManageUsers ? (
                  <Nav.Link onClick={() => handlePageChange('dashboard')}>דאשבורד</Nav.Link>
                ) : null}
                
                <NavDropdown title="אירועים" id="events-nav-dropdown">
                  <NavDropdown.Item onClick={() => handlePageChange('calendar')}>יומן אירועים</NavDropdown.Item>
                  <NavDropdown.Item onClick={() => handlePageChange('events')}>רשימת אירועים</NavDropdown.Item>
                  <NavDropdown.Item onClick={() => handlePageChange('events-search')}>חיפוש וסינון אירועים</NavDropdown.Item>
                  {canEditEvents && (
                    <>
                      <NavDropdown.Item onClick={() => handlePageChange('event-preparation')}>הכנת אירועים</NavDropdown.Item>
                      <NavDropdown.Divider />
                      <NavDropdown.Item onClick={() => handlePageChange('manage-event-equipment')}>ניהול ציוד לאירועים</NavDropdown.Item>
                    </>
                  )}
                </NavDropdown>

                {canEditEquipment && (
                  <NavDropdown title="ציוד" id="equipment-nav-dropdown">
                    <NavDropdown.Item onClick={() => handlePageChange('equipment')}>ציוד</NavDropdown.Item>
                    <NavDropdown.Item onClick={() => handlePageChange('defective-equipment')}>ניהול ציוד תקול</NavDropdown.Item>
                    <NavDropdown.Item onClick={() => handlePageChange('consumables')}>מתכלים</NavDropdown.Item>
                    <NavDropdown.Item onClick={() => handlePageChange('event-specifications')}>מפרטי ציוד קבועים</NavDropdown.Item>
                  </NavDropdown>
                )}

                {(canEditEquipment || canManageUsers) && (
                  <NavDropdown title="ניהול" id="management-nav-dropdown">
                    {canEditEquipment && (
                      <>
                        <NavDropdown.Item onClick={() => handlePageChange('clients')}>לקוחות</NavDropdown.Item>
                        <NavDropdown.Item onClick={() => handlePageChange('locations')}>מיקומים</NavDropdown.Item>
                        <NavDropdown.Item onClick={() => handlePageChange('categories')}>קטגוריות</NavDropdown.Item>
                        <NavDropdown.Item onClick={() => handlePageChange('event-colors')}>צבעים</NavDropdown.Item>
                      </>
                    )}
                    {canManageUsers && (
                      <>
                        {canEditEquipment && <NavDropdown.Divider />}
                        <NavDropdown.Item onClick={() => handlePageChange('user-management')}>ניהול משתמשים</NavDropdown.Item>
                      </>
                    )}
                  </NavDropdown>
                )}

              </Nav>
              <Nav className="ms-auto align-items-center gap-2">
                <Button
                  variant={isDarkMode ? 'outline-light' : 'outline-secondary'}
                  size="sm"
                  onClick={() => setIsDarkMode((prev) => !prev)}
                  title={isDarkMode ? 'מעבר למצב יום' : 'מעבר למצב לילה'}
                >
                  <i className={`fas ${isDarkMode ? 'fa-sun' : 'fa-moon'} me-1`}></i>
                  {isDarkMode ? 'מצב יום' : 'מצב לילה'}
                </Button>
                {isAuthenticated ? (
                  <>
                    <NavDropdown title={user?.full_name || user?.username || 'משתמש'} id="user-nav-dropdown" align="end">
                      <NavDropdown.ItemText>
                        <small className="text-muted">
                          תפקיד: {user?.role?.display_name || 'לא הוגדר'}
                        </small>
                      </NavDropdown.ItemText>
                      <NavDropdown.Divider />
                      <NavDropdown.Item onClick={() => navigate('/change-password')}>
                        <i className="fas fa-key"></i> שינוי סיסמה
                      </NavDropdown.Item>
                    </NavDropdown>
                    <Button variant="outline-danger" size="sm" onClick={handleLogout}>
                      <i className="fas fa-sign-out-alt me-1"></i>
                      התנתקות
                    </Button>
                  </>
                ) : (
                  <>
                    <Nav.Link onClick={() => navigate('/login')}>התחברות</Nav.Link>
                    <Nav.Link onClick={() => navigate('/register')}>הרשמה</Nav.Link>
                  </>
                )}
              </Nav>
            </Navbar.Collapse>
          </Container>
        </Navbar>
      )}

      <Container fluid className={shouldHideNavbar ? 'mt-4' : 'app-main-below-fixed-navbar'}>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />

          {/* Protected routes */}
          <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
          <Route path="/user-management" element={
            <ProtectedRoute requireAdmin={true}>
              <UserManagement />
            </ProtectedRoute>
          } />
          <Route path="/calendar" element={
            <ProtectedRoute>
              <EventCalendar 
                onEventClick={handleNavigateToEventDetails} 
                onCreateNewEvent={handleCreateNewEvent} 
                onGoToEventList={() => handlePageChange('events')} 
              />
            </ProtectedRoute>
          } />
          <Route path="/event-preparation" element={
            <ProtectedRoute>
              {canEditEvents ? (
                <EventPreparation 
                  onBackToList={() => handlePageChange('events')} 
                  onBackToCalendar={() => handlePageChange('calendar')} 
                  onBackToDetails={(eventId) => navigate(`/event-details/${eventId}`)} 
                />
              ) : (
                <div className="text-center mt-5">
                  <h3>אין לך הרשאה לגשת לעמוד זה</h3>
                  <p>רק מנהלים, הרצה, מחסנאים וראשי צוות יכולים לגשת לעמוד הכנת אירועים</p>
                </div>
              )}
            </ProtectedRoute>
          } />
          <Route path="/event-preparation/:eventId" element={
            <ProtectedRoute>
              {canEditEvents ? (
                <EventPreparation 
                  onBackToList={() => handlePageChange('events')} 
                  onBackToCalendar={() => handlePageChange('calendar')} 
                  onBackToDetails={(eventId) => navigate(`/event-details/${eventId}`)} 
                />
              ) : (
                <div className="text-center mt-5">
                  <h3>אין לך הרשאה לגשת לעמוד זה</h3>
                  <p>רק מנהלים, הרצה, מחסנאים וראשי צוות יכולים לגשת לעמוד הכנת אירועים</p>
                </div>
              )}
            </ProtectedRoute>
          } />
          <Route path="/events" element={
            <ProtectedRoute>
              <EventList 
                onManageEquipment={handleNavigateToEquipmentManager} 
                onPrepareEvent={handleNavigateToEventPreparation} 
                onShowDetails={handleNavigateToEventDetails} 
                onGoToCalendar={() => handlePageChange('calendar')} 
              />
            </ProtectedRoute>
          } />
          <Route path="/events-search" element={
            <ProtectedRoute>
              <EventsSearchPage 
                onBack={() => handlePageChange('events')}
                onEventDetails={handleNavigateToEventDetails}
                onManageEquipment={handleNavigateToEquipmentManager}
                onEventPreparation={handleNavigateToEventPreparation}
              />
            </ProtectedRoute>
          } />
          <Route path="/equipment" element={<ProtectedRoute><EquipmentList /></ProtectedRoute>} />
          <Route path="/defective-equipment" element={
            <ProtectedRoute>
              <DefectiveEquipmentManagement 
                onBack={() => handlePageChange('equipment')} 
              />
            </ProtectedRoute>
          } />
          <Route path="/clients" element={<ProtectedRoute><ClientList /></ProtectedRoute>} />
          <Route path="/consumables" element={<ProtectedRoute><ConsumablesList /></ProtectedRoute>} />
          <Route path="/event-specifications" element={<ProtectedRoute><EventSpecificationList /></ProtectedRoute>} />
          <Route path="/locations" element={<ProtectedRoute><LocationList /></ProtectedRoute>} />
          <Route path="/categories" element={<ProtectedRoute><CategoryList /></ProtectedRoute>} />
          <Route path="/event-colors" element={<ProtectedRoute><EventColorList /></ProtectedRoute>} />
          <Route path="/manage-event-equipment" element={
            <ProtectedRoute>
              <EventEquipmentManager 
                onBack={() => handlePageChange('events')} 
                onBackToCalendar={() => handlePageChange('calendar')} 
                onBackToDetails={(eventId) => navigate(`/event-details/${eventId}`)} 
              />
            </ProtectedRoute>
          } />
          <Route path="/manage-event-equipment/:eventId" element={
            <ProtectedRoute>
              <EventEquipmentManager 
                onBack={() => handlePageChange('events')} 
                onBackToCalendar={() => handlePageChange('calendar')} 
                onBackToDetails={(eventId) => navigate(`/event-details/${eventId}`)} 
              />
            </ProtectedRoute>
          } />
          <Route path="/event-details/:eventId" element={
            <ProtectedRoute>
              <EventDetailsPage 
                onBackToList={() => handlePageChange('events')} 
                onBackToCalendar={() => handlePageChange('calendar')} 
                onManageEquipment={handleNavigateToEquipmentManager} 
                onPrepareEvent={handleNavigateToEventPreparation} 
              />
            </ProtectedRoute>
          } />
          <Route path="/warehouse-tasks" element={<ProtectedRoute><WarehouseTaskList /></ProtectedRoute>} />
          <Route path="/test" element={<ProtectedRoute><TestPage /></ProtectedRoute>} />
        </Routes>

        <Modal show={showEventFormForNewEvent} onHide={handleNewEventFormCancel} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>הוספת אירוע חדש</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {initialNewEventDates && (
                    <EventForm
                        onSuccess={handleNewEventFormSuccess}
                        onCancel={handleNewEventFormCancel}
                        initialStartDate={initialNewEventDates.startDate}
                        initialEndDate={initialNewEventDates.endDate}
                    />
                )}
            </Modal.Body>
        </Modal>
      </Container>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;
